import type { CopilotMode } from '@fluentui-copilot/react-provider';
import type { ComponentProps, ComponentState, ProgressBar, Slot } from '@fluentui/react-components';

export type CopilotMessageSlots = {
  /**
   * The root slot for CopilotMessage
   */
  root: NonNullable<Slot<'div'>>;

  /*
   * The label slot is only exposed to screen reader users, and is used in two ways:
   * 1. It prepends the text content in the message's accessible name, e.g. "Copilot wrote: [text content]"
   * 2. It is exposed as a heading to facilitate easy heading-based message navigation
   * NOTE: we recommend the text content of this slot to be "Copilot wrote: " or "Copilot wrote at [timestamp]: ".
   */
  accessibleHeading?: Slot<'h6', 'div'>;

  /**
   * The slot for the icon
   */
  avatar: NonNullable<Slot<'div'>>;

  /**
   * The slot for the name
   */
  name: NonNullable<Slot<'div'>>;

  /**
   * The slot for the AI disclaimer
   */
  disclaimer?: Slot<'div'>;

  /**
   * The slot for the message content
   */
  content?: Slot<'div'>;

  /**
   * The slot for the informational footer items
   */
  footnote?: Slot<'div'>;

  /**
   * The slot for the action footer items
   */
  actions?: Slot<'div'>;

  /**
   * The slot for the progress bar that is shown when content is being streamed
   */
  progress?: Slot<typeof ProgressBar>;
};

/**
 * CopilotMessage Props
 */
export type CopilotMessageProps = CopilotMode &
  Omit<ComponentProps<CopilotMessageSlots>, 'content'> &
  Pick<CopilotMessageSlots, 'content'> & {
    /*
     * Defines a screen reader live region message to be fired on any updates to the prop value.
     * By default CopilotMessage announces its full text when it finishes loading, and if announcement is undefined.
     * NOTE: Explicitly setting this prop is recommended for messages that contain repetitive text like "Copy" or AI disclaimers.
     * This should also be explicitly set if the message text is dynamically updated after isLoading is set to false, e.g. if the text is progressively loaded without a progressbar.
     */
    announcement?: string;

    defaultFocused?: boolean;

    /**
     * The loading state of the message's contents.
     * @default none
     */
    loadingState?: 'loading' | 'streaming' | 'none';
  };

/**
 * State used in rendering CopilotMessage
 */
export type CopilotMessageState = ComponentState<CopilotMessageSlots> &
  Required<Pick<CopilotMessageProps, 'mode' | 'loadingState'>> & {
    /**
     * CSS class for the wrapper container around accessible heading, avatar, name, and disclaimer.
     */
    nameLineClassName?: string;
  };
